import React, { useCallback, useEffect, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import ThreeDots from 'icons/ThreeDots';
import LoadingSpinner from 'icons/LoadingSpinner';
import MoreButton from './MoreButton';
import { TooltipContent, TooltipRoot, TooltipTrigger } from 'components/app-components/ui/tooltip';

export interface IMenuItem {
  name: string | React.ReactNode;
  description?: string;
  handleOnClick?: () => void;
  isHidden?: boolean;
  isDisabled?: boolean;
  className?: string;
  isLoading?: boolean;
  preventClose?: boolean;
}

export default function TheraMenu({
  items,
  menuButton,
  className,
  itemsWrapperClassName,
  nakedEllipsis = false,
  open,
  onOpenChange,
}: {
  items: IMenuItem[];
  menuButton?: React.ReactNode;
  className?: string;
  itemsWrapperClassName?: string;
  nakedEllipsis?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const areAllItemsHidden = items.filter((item: IMenuItem) => !item?.isHidden)?.length === 0;

  // Support both controlled (open/onOpenChange passed) and uncontrolled usage so
  // the close-on-scroll behaviour below works in every call site.
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  // On mobile the portaled popper drifts off-screen (above the fixed navbar)
  // when the page scrolls or rubber-bands on iOS. Closing the menu on any
  // outside scroll is the standard fix and keeps it anchored to its trigger.
  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnScroll = (event: Event) => {
      const target = event.target as HTMLElement | null;
      // ignore scrolling that happens inside the menu itself (long lists)
      if (target?.closest?.('[role="menu"],[data-radix-menu-content]')) return;
      setOpen(false);
    };
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', closeOnScroll);
    return () => {
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', closeOnScroll);
    };
  }, [isOpen, setOpen]);

  return (
    <div className={`${className ? className : ''} ${areAllItemsHidden ? 'hidden' : ''}`}>
      <DropdownMenu.Root modal={false} open={isOpen} onOpenChange={setOpen}>
        <div
          className="relative flex justify-center"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <DropdownMenu.Trigger asChild>
            {menuButton ? (
              menuButton
            ) : nakedEllipsis ? (
              <a
                className="flex cursor-pointer items-center rounded-md border border-gray-300 px-4 py-2 hover:bg-gray-50"
                aria-label="More"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <ThreeDots className="h-3 w-3" />
              </a>
            ) : (
              <MoreButton />
            )}
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              side="bottom"
              sideOffset={4}
              avoidCollisions
              collisionPadding={8}
              hideWhenDetached
              className={`z-[100] min-w-[140px] origin-[--radix-dropdown-menu-content-transform-origin] whitespace-nowrap rounded-md border border-transparent bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 focus:outline-none dark:border-neutral-700 ${
                itemsWrapperClassName ? itemsWrapperClassName : ''
              } `}
            >
              <div className="py-1">
                {items
                  .filter((item: IMenuItem) => !item?.isHidden)
                  .map((item, key) => (
                    <DropdownMenu.Item
                      key={key}
                      onSelect={(e) => {
                        if (item.preventClose) e.preventDefault();
                        item.handleOnClick?.();
                      }}
                      className={`${
                        item?.className === null
                          ? ''
                          : `flex px-4 py-2 text-sm text-neutral-600 outline-none hover:cursor-pointer hover:bg-gray-100 ${
                              item?.className ? item?.className : ''
                            }`
                      }`}
                      disabled={item?.isDisabled}
                      aria-label={
                        item?.isLoading ? 'Loading...' : typeof item?.name === 'string' ? item.name : undefined
                      }
                    >
                      {item.isLoading ? (
                        <span className="flex w-full justify-center">
                          <LoadingSpinner height={12} width={12} />
                        </span>
                      ) : item.description ? (
                        <TooltipRoot delayDuration={200}>
                          <TooltipTrigger asChild>
                            <span className="w-full">{item.name}</span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="right"
                            align="center"
                            sideOffset={12}
                            collisionPadding={16}
                            avoidCollisions
                            className="max-w-[320px] whitespace-normal text-xs font-normal leading-snug"
                          >
                            {item.description}
                          </TooltipContent>
                        </TooltipRoot>
                      ) : (
                        item.name
                      )}
                    </DropdownMenu.Item>
                  ))}
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </div>
      </DropdownMenu.Root>
    </div>
  );
}
